// API credits bundled with self-serve Pro/Team per month (SCI-3608). Defined
// once so the pricing copy and the API-console upgrade modal stay in sync;
// actual enforcement lives in scite-api-fastapi.
export const PRO_API_CREDITS = 750

// MCP credits bundled with self-serve Pro per month. Defined here for the same
// reason as PRO_API_CREDITS — so the pricing copy and the API-console upgrade
// modal stay in sync.
export const PRO_MCP_CREDITS = 2500

// SCI-4517 ended free-tier MCP (reversing SCI-3702/3065): the free plan's MCP
// allowance is now 0, enforced in scite-api-fastapi, so /pricing no longer advertises
// a free MCP quota. The "Connect" card stays the entry point to the upgrade ladder for
// people who meet Scite through ChatGPT or Claude, but MCP itself now requires a free
// trial or a paid plan.

// Total papers one collection can hold, by plan tier (SCI-3863). This is the
// single source of truth for the number: the /pricing feature copy below is
// generated from it, and so is the "Add to Collection" over-limit notice, so the
// two can no longer drift apart.
//
// CROSS-REPO CONTRACT: enforcement lives in scite-api-fastapi's
// `dashboard_doi_limits` (config.py). These values must stay in lockstep with it
// — `enterprise` here is that config's `org_user` key, which applies to every
// org-licensed user. Changing a number in one repo without the other means
// /pricing advertises a cap the API does not honour.
export const COLLECTION_PAPER_LIMITS = {
  // Not advertised anywhere on /pricing, but very much enforced: a free user may
  // create ONE collection of up to 100 papers. It lives here so in-product copy
  // can quote the real number instead of borrowing Basic's (PR #8487 review).
  free: 100,
  basic: 1000,
  pro: 10000,
  // Team is access-equivalent to Pro; the Team card reads "Everything in Pro".
  team: 10000,
  enterprise: 50000
}

// Elasticsearch cannot page past its max result window, so ANY single "add these
// search results to a collection" request resolves at most this many papers,
// whatever the plan cap above allows. Enterprise therefore reaches its 50,000
// total across several adds, never in one. Mirrors
// ELASTICSEARCH_MAX_RESULT_WINDOW in scite-api-fastapi's saved_searches/resolver.py.
export const COLLECTION_ADD_RESULTS_LIMIT = 10000

// Pricing copy is English-only, so pin the locale rather than inheriting the
// viewer's (which would render "1.000" in de-DE).
const papers = count => count.toLocaleString('en-US')

export default {
  free: {
    stripePlan: true,
    illustration: 'tag',
    name: 'Free',
    // `subheading` and `usecase` are NOT the Connect card's copy: BillingPanel's
    // PlanCardBody renders them on /users/me/subscription for every free account,
    // where `subheading` sits beside "$0" as a billing descriptor. The Connect
    // card states its own subheading. Only `features` is shared between the two.
    subheading: 'Access Scite data for free',
    usecase: 'See if an article has been supported or contrasted anywhere you read literature online.',
    price: 0,
    // A feature written as `{ text, included: false }` states a boundary rather
    // than an inclusion; /pricing marks it with a ✕ instead of a check. Only
    // FeatureList renders this list — upgradeTargetFor never targets `free`, so
    // BillingPanel's UpgradePlanCard (which understands `text`/`emphasis` but not
    // `included`) never sees the ✕ form.
    features: [
      'MCP access with a free trial or paid plan',
      'Use in ChatGPT, Claude, & more',
      'Grounded in 300M+ papers',
      { text: 'No Assistant or Search access', included: false }
    ],
    CTA: 'Get Started',
    nextPlan: 'premium'
  },
  basic: {
    stripePlan: true,
    mcpCredits: 250,
    illustration: 'lab',
    name: 'Basic',
    subheading: 'USD / month, Billed annually',
    usecase: 'Explore how groups of articles are cited and stay up to date when new citations are published.',
    price: 12.0,
    monthlyPrice: 20.0,
    annualPrice: 144,
    features: [
      'Full Assistant & Search access',
      '250 MCP credits/month',
      'Article Smart Citation reports',
      `Collections up to ${papers(COLLECTION_PAPER_LIMITS.basic)} papers`,
      'Citation alerts & dashboards'
    ],
    CTA: 'Upgrade Now'
  },
  pro: {
    stripePlan: true,
    mcpCredits: PRO_MCP_CREDITS,
    illustration: 'lab',
    name: 'Pro',
    subheading: 'USD / month, Billed annually',
    usecase: 'Advanced AI-powered research tools with higher usage limits.',
    price: 40.0,
    monthlyPrice: 50.0,
    annualPrice: 480,
    apiCredits: PRO_API_CREDITS,
    featureHeader: 'Everything in Basic, plus:',
    features: [
      'API access',
      // SCI-4060 promo: the $60 (750-call) API credit is a one-time inclusion, not a
      // monthly reset. Sits directly under "API access" so it reads as part of that
      // entitlement; `emphasis` bolds the offer (see FeatureText).
      { text: '$60 API credit included, limited time only', emphasis: '$60 API credit' },
      `2,500 MCP credits/month`,
      `Collections up to ${papers(COLLECTION_PAPER_LIMITS.pro)} papers`,
      'Patent, clinical trial, & grant datasets'
    ],
    CTA: 'Upgrade to Pro'
  },
  team: {
    stripePlan: true,
    mcpCredits: 2500,
    illustration: 'lab',
    name: 'Team',
    subheading: 'USD / month, includes 8 seats',
    usecase: 'Pro for your whole team, managed under one subscription.',
    // SCI-4102: flat $250/mo base covering the first `includedSeats`, then
    // `additionalSeatPrice`/seat beyond that. Billed in Stripe as a per-seat price
    // plus a standing seat coupon (stripe.teamSeatCouponID) — see the server's
    // teamSeatDiscounts(). `price`/`monthlyPrice` are the base fee, matching the
    // field convention BillingPanel reads for the other plans.
    price: 250,
    monthlyPrice: 250,
    annualPrice: 2490,
    includedSeats: 8,
    additionalSeatPrice: 50,
    featureHeader: 'Everything in Pro, plus:',
    features: [
      'Private & shared Collections',
      'Team usage analytics',
      'Centralized billing',
      '8 seats included, $50/mo per additional seat',
      '2,500 MCP credits/month/user'
    ],
    CTA: 'Upgrade Now'
  },
  premium: {
    stripePlan: true,
    mcpCredits: 250,
    illustration: 'lab',
    name: 'Basic',
    legacyNote: 'Premium has been renamed to Basic.',
    subheading: 'USD / month, Billed annually',
    usecase: 'Explore how groups of articles are cited and stay up to date when new citations are published.',
    price: 12.0,
    monthlyPrice: 20.0,
    annualPrice: 144,
    featureHeader: 'Features:',
    features: [
      'Unlimited Assistant queries',
      'Full-Text Search',
      'Article Citation Reports',
      'Citation Alerts',
      'Claude & ChatGPT connectors',
      '250 MCP credits/month',
      `${papers(COLLECTION_PAPER_LIMITS.basic)}-paper Collections`
    ],
    CTA: 'Upgrade Now'
  },
  organization: {
    illustration: 'institution',
    name: 'University & Corporate',
    subheading: 'Built for universities, pharmaceutical companies, and other research institutions',
    usecase:
      'Scite pricing is based on the number of faculty, students, and staff at an institution. We offer organization-wide annual licenses with volume-based discounts. Contact us at sales@scite.ai to request a quote or get a demo.',
    featureHeader: 'Everything included in Personal Plan, plus:',
    features: [
      'Flexible access via domain, IP, or email',
      'API and MCP access',
      'Flexible pooled usage',
      'User analytics & reporting',
      'Regulatory & safety datasets (FAERS, MAUDE, 510(k), MHRA)',
      'Advanced AI models',
      'Shared Dashboards & alerts',
      'Enhanced data exports',
      'Dedicated customer success manager',
      '24/7 priority support',
      'SAML/SSO',
      'Enhanced security & data confidentiality'
    ],
    CTA: 'Book a Demo'
  }
}
